import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { track, useTrackOnce } from "@/hooks/useAnalytics";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { ArrowRight, Loader2, Lock, Mail, User } from "lucide-react";

export const Route = createFileRoute("/cadastro")({
  head: () => ({
    meta: [
      { title: "Criar conta — Plataforma 101" },
      {
        name: "description",
        content:
          "Crie sua conta na Plataforma 101 e conclua o pagamento para liberar o acesso a 101 negócios reais.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SignupPage,
});

const CHECKOUT_STORAGE_KEY = "p101.checkout.v1";

function isValidEmail(e: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

function SignupPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ type: "err" | "ok"; text: string } | null>(null);

  useTrackOnce("signup_started");

  // Se já estiver logado, pula para o checkout direto
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/checkout", replace: true });
    });
  }, [navigate]);

  function seedCheckout() {
    try {
      const raw = localStorage.getItem(CHECKOUT_STORAGE_KEY);
      const prev = raw ? JSON.parse(raw) : {};
      localStorage.setItem(
        CHECKOUT_STORAGE_KEY,
        JSON.stringify({ ...prev, name, email, emailConfirm: email }),
      );
    } catch {
      /* noop */
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);

    if (name.trim().length < 3) {
      setMsg({ type: "err", text: "Informe seu nome completo." });
      return;
    }
    if (!isValidEmail(email)) {
      setMsg({ type: "err", text: "Informe um e-mail válido." });
      return;
    }
    if (password.length < 8) {
      setMsg({ type: "err", text: "A senha precisa de pelo menos 8 caracteres." });
      return;
    }
    if (!agree) {
      setMsg({ type: "err", text: "Você precisa aceitar os Termos e a Privacidade." });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { full_name: name.trim() },
          emailRedirectTo: `${window.location.origin}/checkout`,
        },
      });

      if (error) {
        const raw = (error.message || "").toLowerCase();
        if (raw.includes("registered") || raw.includes("already")) {
          setMsg({
            type: "err",
            text: "Este e-mail já possui conta. Faça login para concluir o pagamento.",
          });
        } else {
          setMsg({ type: "err", text: error.message || "Não foi possível criar a conta." });
        }
        setLoading(false);
        return;
      }

      seedCheckout();
      track("signup_completed", { metadata: { has_session: !!data.session } });


      // Se a Supabase retornou sessão (confirmação de e-mail desligada), segue direto
      if (data.session) {
        navigate({ to: "/checkout", replace: true });
        return;
      }

      // Caso confirmação de e-mail esteja ativa: seguimos ao checkout mesmo assim.
      // O webhook do pagamento identifica o usuário pelo e-mail e libera o acesso.
      navigate({ to: "/checkout", replace: true });
    } catch (err: any) {
      setMsg({ type: "err", text: err?.message || "Erro inesperado. Tente novamente." });
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--beige)] font-body text-[var(--navy)]">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-10">
        <Link to="/" className="mb-6 text-sm text-[var(--gray-ink)] hover:text-[var(--navy)]">
          ← Voltar
        </Link>

        <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_20px_60px_-20px_rgba(12,27,51,0.25)] sm:p-8">
          <h1 className="font-display text-2xl font-extrabold sm:text-3xl">
            Crie sua conta
          </h1>
          <p className="mt-2 text-sm text-[var(--gray-ink)]">
            Passo 1 de 2 — depois você conclui o pagamento único de{" "}
            <strong className="text-[var(--navy)]">R$ 47</strong> e o acesso é liberado
            automaticamente.
          </p>

          <button
            type="button"
            disabled={loading}
            onClick={async () => {
              setMsg(null);
              setLoading(true);
              try {
                sessionStorage.setItem("post_login_redirect", "/checkout");
              } catch { /* noop */ }
              const result = await lovable.auth.signInWithOAuth("google", {
                redirect_uri: window.location.origin + "/auth",
              });
              if (result.error) {
                setLoading(false);
                setMsg({ type: "err", text: result.error.message || "Não foi possível entrar com o Google." });
                return;
              }
              if (result.redirected) return;
              navigate({ to: "/checkout", replace: true });
            }}
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-lg border border-black/10 bg-white px-4 py-3 text-sm font-semibold text-[var(--navy)] transition hover:-translate-y-0.5 hover:border-black/20 hover:shadow-sm disabled:pointer-events-none disabled:opacity-60"
            aria-label="Continuar com Google"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.1-5.2C29.2 34.8 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.1 5.2C41 34.1 44 29.5 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continuar com Google
          </button>

          <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-wide text-[var(--gray-ink)]">
            <span className="h-px flex-1 bg-black/10" />
            ou com e-mail
            <span className="h-px flex-1 bg-black/10" />
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <Field
              icon={<User size={16} />}
              label="Nome completo"
              value={name}
              onChange={setName}
              type="text"
              autoComplete="name"
              placeholder="Como devemos te chamar"
            />
            <Field
              icon={<Mail size={16} />}
              label="E-mail"
              value={email}
              onChange={setEmail}
              type="email"
              autoComplete="email"
              placeholder="seu@email.com"
            />
            <Field
              icon={<Lock size={16} />}
              label="Senha"
              value={password}
              onChange={setPassword}
              type="password"
              autoComplete="new-password"
              placeholder="Mínimo 8 caracteres"
            />

            <label className="flex items-start gap-2 text-xs text-[var(--gray-ink)]">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-black/20"
              />
              <span>
                Li e aceito os{" "}
                <a href="/termos" target="_blank" className="underline hover:text-[var(--navy)]">
                  Termos de uso
                </a>{" "}
                e a{" "}
                <a href="/privacidade" target="_blank" className="underline hover:text-[var(--navy)]">
                  Política de privacidade
                </a>
                .
              </span>
            </label>

            {msg && (
              <p
                className={`rounded-md px-3 py-2 text-sm ${
                  msg.type === "err"
                    ? "bg-red-50 text-red-700"
                    : "bg-emerald-50 text-emerald-700"
                }`}
              >
                {msg.text}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white shadow-[0_10px_28px_-10px_rgba(12,27,51,0.6)] transition hover:scale-[1.01] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Criando conta…
                </>
              ) : (
                <>
                  Criar conta e ir para o pagamento
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <p className="mt-5 text-center text-xs text-[var(--gray-ink)]">
            Já tem uma conta?{" "}
            <Link
              to="/auth"
              className="font-semibold text-[var(--navy)] underline underline-offset-2"
            >
              Entrar
            </Link>
          </p>
        </div>

        <p className="mt-4 px-2 text-center text-[11px] text-[var(--gray-ink)]">
          Seu acesso só é liberado após a confirmação do pagamento. Se você fizer o cadastro e
          não concluir a compra, o login fica bloqueado até a liberação.
        </p>
      </div>
    </div>
  );
}

function Field({
  icon,
  label,
  value,
  onChange,
  type,
  autoComplete,
  placeholder,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type: string;
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        {label}
      </span>
      <div className="flex items-center gap-2 rounded-md border border-black/10 bg-white px-3 py-2 focus-within:border-[var(--navy)]">
        <span className="text-[var(--gray-ink)]">{icon}</span>
        <input
          className="w-full bg-transparent text-sm outline-none"
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          placeholder={placeholder}
        />
      </div>
    </label>
  );
}
